/**
 * TODO:
 * This component could use an abstraction to be unified with the
 * existing component `LastCommentListItem`
 */
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag/V1';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import Image from 'components/Image';
import TextWithEllipsis from 'components/TextWithEllipsisWithShowMore';
import TextWithRichContent from 'components/TextWithRichContent';

import { getTimeTo } from 'lib/date';
import toLastCommentText from 'lib/htmlParsers/parseLastCommentsHtml';

const TAG_TYPE_UNIQUE = 'UNIQUE';

const InteractionsListItem = ({
  blockTitle = null,
  comments = null,
  date,
  href = null,
  image = null,
  imageAlt = null,
  likesCount = null,
  tags = [],
  text = null,
  title,
  views = null,
}) => {
  const Component = (
    <div className="flex w-full border-b border-solid border-neutral-high-200 p-4 dark:border-neutral-low-300">
      {image ? (
        <Image
          alt={imageAlt}
          className="min-w-20 overflow-y-hidden border border-solid border-neutral-high-200 object-cover dark:border-neutral-low-300 md:size-[100px] md:min-w-[100px]"
          height={80}
          src={image}
          width={80}
        />
      ) : (
        <div className="flex size-20 min-w-20 items-center justify-center bg-neutral-high-200 md:size-[100px] md:min-w-[100px]">
          <Icon
            className="w-[38px] text-neutral-high-400 dark:text-neutral-high-400"
            name="messages"
            size={null}
          />
        </div>
      )}
      <div className="ml-4 flex w-full flex-col items-start justify-between">
        <div className="flex w-full flex-col items-start">
          {blockTitle && (
            <NextLink href={href} prefetch={false} passHref>
              <Box
                as="a"
                className="flex-row justify-between bg-neutral-high-200 px-4 py-2 dark:bg-neutral-low-300"
                full
              >
                <div className="md:flex">
                  <TextWithEllipsis
                    className="mr-4 line-clamp-2 md:ml-1 md:line-clamp-1"
                    bold
                  >
                    {blockTitle}
                  </TextWithEllipsis>
                </div>
                <Icon className="text-neutral-low-500" name="link-blank" />
              </Box>
            </NextLink>
          )}
          {title && (
            <Heading as="span" size="size6">
              {title}
            </Heading>
          )}
          <Text className="mt-2" size="size3">
            {getTimeTo(date)}
          </Text>
          {text && (
            <TextWithRichContent className="mt-2" full>
              {toLastCommentText(text)}
            </TextWithRichContent>
          )}
          {tags.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center">
              {tags.map((tag) => {
                const tagName = tag?.name || tag;

                return tagName === TAG_TYPE_UNIQUE ? (
                  <Text
                    key={tagName}
                    className="mr-2 text-success-300 dark:text-success-100"
                    size="size2"
                  >
                    {tagName}
                  </Text>
                ) : (
                  <Tag key={tagName} className="mr-2" filled>
                    {tagName}
                  </Tag>
                );
              })}
            </div>
          )}
        </div>
        {(comments > 0 || likesCount > 0 || views > 0) && (
          <div className="mt-6 flex w-full justify-end">
            {comments > 0 && (
              <div className="flex items-center">
                <Icon
                  className="mr-2 text-neutral-low-500 dark:text-neutral-high-100"
                  name="messages"
                />
                <Text size="size2">{comments}</Text>
              </div>
            )}
            {likesCount > 0 && (
              <div className="flex items-center">
                <Icon
                  className="ml-5 text-neutral-low-500 dark:text-neutral-high-100"
                  name="like-filled"
                />
                <Text size="size2">{likesCount}</Text>
              </div>
            )}
            {views > 0 && (
              <div className="ml-5 flex items-center">
                <Icon
                  className="mr-2 text-neutral-low-500 dark:text-neutral-high-100"
                  name="visibility"
                />
                <Text size="size2">{views.toLocaleString('pt-BR')}</Text>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <NextLink href={href} prefetch={false} passHref>
        <Anchor className="w-full">{Component}</Anchor>
      </NextLink>
    );
  }

  return Component;
};

InteractionsListItem.propTypes = {
  blockTitle: PropTypes.string,
  comments: PropTypes.number,
  date: PropTypes.string.isRequired,
  href: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  likesCount: PropTypes.number,
  tags: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ]).isRequired
  ),
  text: PropTypes.string,
  title: PropTypes.string.isRequired,
  views: PropTypes.number,
};

export default InteractionsListItem;
